import { useEffect } from 'react';

import useSnackbar from 'src/hooks/useSnackbar';
import { useGetPayPeriodsQuery } from 'src/services/PayPeriods';
import { PayPeriod } from 'src/types/resources/PayPeriod';
import { getCurrentPeriod, getPreviousPeriod } from 'src/utils/payPeriods';

type UsePayPeriods = {
  periods: PayPeriod[];
  currentPeriod: PayPeriod | undefined;
  previousPeriod: PayPeriod | undefined;
  isLoading: boolean;
  isError: boolean;
};

const EMPTY_PERIODS: PayPeriod[] = [];
const PAY_PERIODS_ERROR_SNACKBAR_KEY = 'payPeriodsLoadError';
const NO_CURRENT_PAY_PERIOD_SNACKBAR_KEY = 'noCurrentPayPeriod';

const usePayPeriods = (): UsePayPeriods => {
  const { enqueueErrorSnackbar } = useSnackbar();
  const {
    data = EMPTY_PERIODS,
    isLoading,
    isFetching,
    isSuccess,
    isError,
  } = useGetPayPeriodsQuery(undefined, { refetchOnFocus: true });

  const currentPeriod = getCurrentPeriod(data);
  const previousPeriod = currentPeriod && getPreviousPeriod(data, currentPeriod);
  const isCurrentPeriodMissing = isSuccess && !isFetching && !currentPeriod;

  useEffect(() => {
    if (isError) {
      enqueueErrorSnackbar('Failed to load pay periods', {
        key: PAY_PERIODS_ERROR_SNACKBAR_KEY,
        preventDuplicate: true,
      });
    }
  }, [isError]);

  useEffect(() => {
    if (isCurrentPeriodMissing) {
      enqueueErrorSnackbar('No open pay period found', {
        key: NO_CURRENT_PAY_PERIOD_SNACKBAR_KEY,
        preventDuplicate: true,
      });
    }
  }, [isCurrentPeriodMissing]);

  return { periods: data, currentPeriod, previousPeriod, isLoading, isError };
};

export default usePayPeriods;
